import { useContext, useEffect, useId, useRef, useState } from "react"
import { Modal } from "react-bootstrap"
import { ReactReduxContext } from "react-redux";
import clsx from "clsx";
import PropTypes from 'prop-types';
import { Localize } from "../../../shared/ariaLabelLocalize";
import IconTooltip, { ICON_TOOLTIP_HOST } from "../Tooltip/IconTooltip";

const FOCUSABLE_SELECTOR =
    'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

// When a modal opens, we can't just cache the trigger element — many callers
// re-fetch the underlying list on save/delete, which unmounts the original DOM
// node and replaces it with a fresh one that shares the same id. A cached
// element reference then points to a detached node and `.focus()` silently
// no-ops. Instead capture a *lookup* that re-resolves the trigger at close
// time. When the modal is opened from a dropdown menu item (which itself
// unmounts as the menu closes), read the menu's `aria-labelledby` to jump to
// the stable trigger button that opened the menu.
const buildReturnLookup = (element) => {
    if (!element || element === document.body) return null;
    const menu = element.closest('[role="menu"], .dropdown-menu');
    if (menu) {
        const labelledBy = menu.getAttribute('aria-labelledby');
        if (labelledBy) return () => document.getElementById(labelledBy);
        const dropdown = menu.closest('.dropdown, .dropup');
        if (dropdown) {
            // Prefer a stable id if the toggle has one, else fall back to a live
            // querySelector so a re-rendered toggle is still found.
            const toggle = dropdown.querySelector('.dropdown-toggle, [aria-haspopup]');
            if (toggle?.id) {
                const id = toggle.id;
                return () => document.getElementById(id);
            }
            if (dropdown.id) {
                const id = dropdown.id;
                return () => document.getElementById(id)?.querySelector('.dropdown-toggle, [aria-haspopup]') || null;
            }
        }
    }
    // Direct trigger: if it has an id, re-resolve by id (survives re-renders);
    // otherwise cache the element reference itself and hope it stays mounted.
    if (element.id) {
        const id = element.id;
        return () => document.getElementById(id);
    }
    return () => (document.body.contains(element) ? element : null);
};

// Mimic `:focus-visible` for programmatic focus. The heuristic browsers use to
// decide whether to show a focus ring for `.focus()` calls is inconsistent —
// especially after a modality drift (e.g. mouse-open + Esc-close). Add a
// class-based ring that self-removes on blur/pointerdown so restored focus is
// always visible, without changing behaviour for genuine mouse-focus flows.
const FOCUS_RESTORED_CLASS = 'a11y-focus-restored';

// Track the modality of the user's last input so we only paint the restored
// focus ring for keyboard users. Mouse users closing a modal with a click
// shouldn't see a lingering ring on the heading focus lands on — reported
// on `.header-portal-name` after mouse-close of CreateSession, and on the
// SelfSignup CompanyInfo heading. Register once (module-scoped) and use
// capture phase so we win over anything else that stops propagation.
let lastInputWasKeyboard = false;
if (typeof window !== 'undefined') {
    window.addEventListener('keydown', () => { lastInputWasKeyboard = true; }, true);
    window.addEventListener('pointerdown', () => { lastInputWasKeyboard = false; }, true);
    window.addEventListener('mousedown', () => { lastInputWasKeyboard = false; }, true);
    window.addEventListener('touchstart', () => { lastInputWasKeyboard = false; }, true);
}

const armFocusRestoredClass = (target) => {
    if (!target || !target.classList) return;
    // Mouse/touch users don't want a focus ring on programmatically restored
    // focus — matches native `:focus-visible` behaviour (which suppresses the
    // ring after a pointer interaction).
    if (!lastInputWasKeyboard) return;
    target.classList.add(FOCUS_RESTORED_CLASS);
    const cleanup = () => {
        target.classList.remove(FOCUS_RESTORED_CLASS);
        target.removeEventListener('blur', cleanup);
        target.removeEventListener('pointerdown', cleanup);
    };
    target.addEventListener('blur', cleanup);
    target.addEventListener('pointerdown', cleanup);
};

const restoreFocusTo = (target) => {
    if (!target || !document.body.contains(target)) return false;
    // Some triggers (e.g. `<a>` items) aren't natively focusable — force tabindex
    // so `.focus()` actually moves focus.
    if (!target.matches(FOCUSABLE_SELECTOR) && !target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
    }
    // `preventScroll: true` avoids the browser scrolling ancestor containers to
    // bring the target into view — that quirk can push a sticky top header out
    // of the viewport on pages where `.app-with-sidebar__right` clips overflow
    // (reported after closing CreateSessionModal on `/create`).
    try {
        target.focus({ preventScroll: true });
    } catch (_) {
        target.focus();
    }
    if (document.activeElement !== target) {
        // A hidden ancestor or a still-transitioning modal may have swallowed
        // the focus attempt — scroll into view (block: 'nearest' so we only
        // move enough to reveal the target) and retry once.
        if (typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
        try {
            target.focus({ preventScroll: true });
        } catch (_) {
            target.focus();
        }
    }
    if (document.activeElement === target) {
        armFocusRestoredClass(target);
        return true;
    }
    return false;
};

export const PopupModal = ({ modalClass, dialogClass, title, titleId, ariaLabel, headerContent = null, bodyContent, footerContent = null, onConfirm, bodyClass, footerClass,
    confirmText = "Confirm", cancelText = "Cancel", cancelButton = true, closeButton = true, nextArraow = false,
    backdrop = true, disableConfirm = true, showLoader = false, disablekeyboard = false,
    returnFocusRef,
    ...otherProps }) => {
    const [showPopup, setShowPopup] = useState(true);
    const modalNodeRef = useRef(null);
    // Read language via ReactReduxContext directly so unit tests that render
    // PopupModal without a <Provider> still work — useSelector would throw.
    const reduxContext = useContext(ReactReduxContext);
    const languageId = reduxContext?.store?.getState()?.language?.selectedlanguage?.id;
    const localised = Localize[languageId] || Localize['en_US'];
    // Capture a *lookup function* synchronously in the render body via a lazy
    // state initializer — it runs once at first render, BEFORE React commits the
    // DOM and before focus moves into the dialog on enter. The lookup re-resolves the
    // trigger by id at close time so a re-fetched list that swaps out the row's
    // DOM doesn't leave us with a stale element reference.
    const [returnLookup] = useState(() =>
        returnFocusRef?.current ? null : buildReturnLookup(document.activeElement)
    );
    const generatedTitleId = useId();
    const resolvedTitleId = titleId ?? generatedTitleId;

    const onCloseModal = () => {
        setShowPopup(false);
        otherProps.closeModal();
    }

    const handleEntering = (node) => {
        modalNodeRef.current = node;
    };

    const handleEntered = (node) => {
        if (!node) return;
        const active = document.activeElement;
        if (active && active !== node && node.contains(active)) return;
        const preferred = node.querySelector('[data-autofocus]');
        if (preferred) {
            preferred.focus();
            if (document.activeElement === preferred) return;
        }
        const closeBtn = node.querySelector('.btn-close');
        if (closeBtn) {
            closeBtn.focus();
            if (document.activeElement === closeBtn) return;
        }
        node.focus();
    };

    // Restore focus when the modal unmounts. Prefer an explicit `returnFocusRef`
    // if the caller supplied one and it's still in DOM; otherwise re-run the
    // captured lookup to find the current trigger element. Falls back to a page
    // landmark if both targets are gone (e.g. list re-fetched away the row
    // AND the caller didn't pass an explicit ref).
    useEffect(() => {
        return () => {
            const raf = (typeof requestAnimationFrame === 'function')
                ? requestAnimationFrame
                : (cb) => setTimeout(cb, 0);
            const resolveTarget = () => {
                const explicit = returnFocusRef?.current;
                if (explicit && document.body.contains(explicit)) return explicit;
                if (returnLookup) return returnLookup();
                return null;
            };
            // Wait one frame so react-bootstrap's own focus teardown finishes
            // before we reclaim focus — otherwise the dialog's exit animation can
            // steal it back to <body>. Do the lookup INSIDE the raf so any list
            // re-render triggered by the close (e.g. delete + refetch) has a
            // chance to commit before we resolve.
            raf(() => {
                let target = resolveTarget();
                if (!target || !restoreFocusTo(target)) {
                    const heading = document.querySelector('h1, [data-page-heading], main');
                    if (heading) restoreFocusTo(heading);
                    return;
                }
                // Guard against late focus-steal: some wrappers (IntlTelInput,
                // deferred re-renders, third-party libs) can move focus to
                // <body> a beat after unmount. `focusin` on body is unreliable
                // across browsers, so poll activeElement instead. Bail on any
                // real user gesture — we only want to catch the involuntary drop.
                const start = Date.now();
                const GUARD_MS = 1500;
                const POLL_MS = 50;
                let userTookOver = false;
                const isDocDefault = (el) => !el || el === document.body || el === document.documentElement;
                const poll = setInterval(() => {
                    if (userTookOver || Date.now() - start > GUARD_MS) {
                        clearInterval(poll);
                        document.removeEventListener('keydown', onUserGesture, true);
                        document.removeEventListener('mousedown', onUserGesture, true);
                        return;
                    }
                    if (!isDocDefault(document.activeElement)) return;
                    const fresh = resolveTarget();
                    if (fresh && fresh !== target) target = fresh;
                    if (target) restoreFocusTo(target);
                }, POLL_MS);
                const onUserGesture = () => {
                    // Ignore the click event that closed the modal (fires at t≈0).
                    if (Date.now() - start < 50) return;
                    userTookOver = true;
                };
                document.addEventListener('keydown', onUserGesture, true);
                document.addEventListener('mousedown', onUserGesture, true);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Trap Tab / Shift+Tab within the modal so focus can't escape to browser chrome
    useEffect(() => {
        if (!showPopup) return undefined;
        const handleKeyDown = (e) => {
            if (e.key !== "Tab") return;
            // Fall back to a DOM lookup if the react-bootstrap transition hasn't set
            // the ref yet. `querySelectorAll(...).at(-1)` grabs the top-most modal in
            // DOM order so a stacked scenario (modal opened from within another) picks
            // the correct container instead of always the first.
            const container =
                modalNodeRef.current ||
                Array.from(document.querySelectorAll('[data-testid="popupModal"]')).at(-1);
            if (!container) return;
            // Only handle Tab when focus is actually inside THIS modal — prevents
            // stacked-modal handlers competing for the same Tab press.
            if (!container.contains(document.activeElement)) return;
            const focusables = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
                (el) => el.getAttribute("aria-hidden") !== "true"
            );
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;
            if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [showPopup]);

    const confirmButtonStyle = clsx(
        "btn btn-primary font-weight-500 btn-with-icon",
        nextArraow && "btn-with-icon next-btn"
    );

    let modalLabelProps = {};
    if (title || titleId || headerContent) {
        modalLabelProps = { 'aria-labelledby': resolvedTitleId };
    } else if (ariaLabel) {
        modalLabelProps = { 'aria-label': ariaLabel };
    }

    return (
        <Modal
            show={showPopup}
            onHide={onCloseModal}
            onEntering={handleEntering}
            onEntered={handleEntered}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={false}
            className={modalClass ? modalClass : ""}
            dialogClassName={dialogClass ? dialogClass : ""}
            backdrop={backdrop}
            data-testid="popupModal"
            keyboard={!disablekeyboard}
            restoreFocus={false}
            {...modalLabelProps}
        >
            <Modal.Header>
                {title
                    ? <Modal.Title id={resolvedTitleId}>{title}</Modal.Title>
                    : headerContent && !titleId
                        ? <div id={resolvedTitleId}>{headerContent}</div>
                        : headerContent}
                {closeButton &&
                    <button type="button" className={`btn-close ${ICON_TOOLTIP_HOST}`} aria-label={localised.close} onClick={onCloseModal} data-testid="modalCloseIcon">
                        <span className="icon-close" aria-hidden="true"></span>
                        <IconTooltip label={localised.close} />
                    </button>
                }
            </Modal.Header>
            <Modal.Body className={bodyClass ? bodyClass : ""}>
                {bodyContent}
            </Modal.Body>
            <Modal.Footer
                className={footerClass ? footerClass : ""}
            >
                <div className={clsx("button-inline d-flex", otherProps.footerInlineClass ?? '')}>
                    {footerContent ??
                        <>
                            {cancelButton &&
                                <div className="button">
                                    <button type="button" className="btn btn-outline-secondary font-weight-500" onClick={onCloseModal} data-testid="modalCloseButton">{cancelText}</button>
                                </div>
                            }
                            <div className="button">
                                <button type="button" className={confirmButtonStyle} onClick={onConfirm} disabled={disableConfirm} data-testid="modalSendButton">
                                    {showLoader && <span className="btn-loader-icon"><span className="for-icon icon-loader spin" aria-hidden="true"></span></span>}{confirmText}{nextArraow && <span className="btn-next-arrow icon-right-arrow" aria-hidden="true"></span>}
                                </button>
                            </div>
                        </>
                    }
                </div>
            </Modal.Footer>
        </Modal>
    );
}

PopupModal.propTypes = {
    backdrop: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    disableConfirm: PropTypes.bool,
    showLoader: PropTypes.bool,
    disablekeyboard: PropTypes.bool,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    cancelButton: PropTypes.bool,
    closeButton: PropTypes.bool,
    nextArraow: PropTypes.bool,
    modalClass: PropTypes.string,
    dialogClass: PropTypes.string,
    bodyClass: PropTypes.string,
    footerClass: PropTypes.string,
    title: PropTypes.node,
    titleId: PropTypes.string,
    ariaLabel: PropTypes.string,
    headerContent: PropTypes.any,
    bodyContent: PropTypes.any,
    footerContent: PropTypes.any,
    onConfirm: PropTypes.func,
    closeModal: PropTypes.func,
    footerInlineClass: PropTypes.string,
    returnFocusRef: PropTypes.shape({ current: PropTypes.any }),
}
